import React, { useState } from 'react';
import {
  auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from '../firebaseConfig';
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import styles from './Login.module.css';
import { FaGoogle, FaFacebookF } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Cloudinary hosted images
const logo = 'https://res.cloudinary.com/dqushvncg/image/upload/v1765434267/logo_zvkjeg.png';
const loginImage = 'https://res.cloudinary.com/dqushvncg/image/upload/v1765434267/login_Image_f3rt92.webp';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  
  /**
   * Sync user to backend - first checks if user exists, creates only if not found
   */
  const syncUserToBackend = async (user) => {
    if (!user || !user.email) {
      return;
    }
    
    try {
      // First, try to fetch existing user
      const getResponse = await fetch(
        `${BACKEND_URL}/api/users?email=${encodeURIComponent(user.email)}`
      );
      
      if (getResponse.ok) {
        // User already exists, no need to create
        return await getResponse.json();
      }
      
      // User not found (404), create new user
      if (getResponse.status === 404) {
        const payload = {
          email: user.email,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          profilePicture: user.photoURL || '',
          firebaseUid: user.uid,
          roles: ['player'],
          isVerified: user.emailVerified || false
        };
        
        const createResponse = await fetch(`${BACKEND_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        if (createResponse.ok) {
          return await createResponse.json();
        }
      }
      // Don't block login if backend sync fails
    } catch (err) {
      // Don't block login if backend sync fails
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await syncUserToBackend(result.user);
      navigate('/');
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const signInWithFacebook = async () => {
    setLoading(true);
    setError(null);
    const provider = new FacebookAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await syncUserToBackend(result.user);
      navigate('/');
    } catch (error) {
      setError('Failed to sign in with Facebook. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if email is verified (only for email/password users)
      if (!user.emailVerified) {
        await auth.signOut();
        setError('Please verify your email before logging in. Check your inbox for the verification link.');
        return;
      }
      
      // If verified, sync user to backend and proceed
      await syncUserToBackend(user);
      navigate('/');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      
      // Don't sign out — the user stays authenticated but unverified.
      // App.js will detect emailVerified=false and show EmailVerificationScreen
      // which has auto-check, resend, and sign-out options.
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please try logging in instead.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password (at least 6 characters).');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/operation-not-allowed':
          setError('Email/password accounts are not enabled. Please contact support.');
          break;
        default:
          setError('Sign-up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPasswordModal(true);
    setResetEmail('');
    setError(null);
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Please enter your email to reset the password.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert('Password reset email sent!');
      setShowForgotPasswordModal(false);
      setResetEmail('');
    } catch (error) {
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setResetEmail('');
    setError(null);
  };

  return (
    <div className={styles.loginContainer}>
      {/* Left Side - Image */}
      <div className={styles.imageSection} style={{ '--login-image': `url(${loginImage})` }}>
        <div className={styles.imageOverlay}>
          <div className={styles.brandContent}>
            <h1 className={styles.brandTitle}>
                              <img src={logo} alt="Sport Loop Icon" className={styles.titleIcon} />
                Sport Loop
            </h1>
            <p className={styles.brandSubtitle}>Connect with sports clubs and communities</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className={styles.formSection}>
        <div className={styles.formContainer}>
          <h2 className={styles.loginTitle}>{isSignUp ? 'Create Account' : 'Welcome Back!'}</h2>
          <p className={styles.loginSubtitle}>
            {isSignUp ? 'Join the sports community today' : 'Sign in to your account'}
          </p>

          {isSignUp ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSignUp(); }} className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Email Address</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Password</label>
                <input
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Confirm Password</label>
                <input
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <button
                type="submit"
                className={styles.actionButton}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
              <p className={styles.toggleText}>
                Already have an account?{' '}
                <span onClick={() => setIsSignUp(false)} className={styles.linkText}>
                  Sign In
                </span>
              </p>
            </form>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleEmailSignIn(); }} className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Email Address</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Password</label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <button
                type="submit"
                className={styles.actionButton}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
              <p className={styles.toggleText}>
                Don't have an account?{' '}
                <span onClick={() => setIsSignUp(true)} className={styles.linkText}>
                  Sign Up
                </span>
              </p>
              <p className={styles.forgotPassword} onClick={handleForgotPassword}>
                Forgot your password?
              </p>
            </form>
          )}

          <div className={styles.divider}>
            <span>or continue with</span>
          </div>

          <div className={styles.socialLogin}>
            <button
              className={`${styles.socialButton} ${styles.googleButton}`}
              onClick={signInWithGoogle}
              disabled={loading}
            >
              <FaGoogle className={styles.socialIcon} />
              Google
            </button>
            {/* Facebook button temporarily hidden - implementation code kept for future use */}
            {/* <button
              className={`${styles.socialButton} ${styles.facebookButton}`}
              onClick={signInWithFacebook}
              disabled={loading}
            >
              <FaFacebookF className={styles.socialIcon} />
              Facebook
            </button> */}
          </div>

          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Reset Password</h3>
            <p>Enter your email address to receive a password reset link.</p>
            <input
              type="email"
              placeholder="Enter your email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className={styles.input}
              required
            />
            <div className={styles.modalButtons}>
              <button
                type="button"
                className={styles.modalButton}
                onClick={handlePasswordReset}
                disabled={!resetEmail || loading}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
              <button
                type="button"
                className={`${styles.modalButton} ${styles.cancelButton}`}
                onClick={closeForgotPasswordModal}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
